import {
  parseActiveRopaReviewMetricsForFE,
  parseAssetRopaReviewForFE,
  parseInitiatedByDisplayName,
} from 'config/api/ropaReviews/ropaReviews.parsers';
import type {
  ActiveRopaReviewMetricsDTO,
  AdminAssetRopaReviewListDTO,
  RopaReviewRoundStatus,
} from 'config/api/ropaReviews/ropaReviews.types';

import type { OwnerAssetRopaListResponse, OwnerAssetRopaMetrics, OwnerAssetRopaMetricsResponse } from './assetRopa.types';
import {
  isOwnerAssetRopaDetailPayload,
  isPermanentWorkspaceAssetPayload,
  parsePermanentWorkspaceAssetDetailForFE,
  parsePermanentWorkspaceListItemForFE,
  resolveMineDetailRecord,
} from './assetRopaPermanentWorkspace.parsers';
import type { OwnerAssetRopaListItemDTO } from './assetRopaPermanentWorkspace.types';
import type { OwnerWorkspaceAsset, OwnerWorkspaceListResponse, OwnerWorkspaceMetrics } from './assetRopaWorkspace.types';

const WORKSPACE_METRIC_KEYS = [
  'gdpr_relevant_assets_count',
  'assets_without_processing_activities_count',
  'incomplete_processings_count',
  'dpia_indications_count',
  'third_country_processing_count',
  'sensitive_personal_data_count',
] as const;

const REVIEW_CARD_METRIC_KEYS = [
  'included_assets_count',
  'awaiting_response_count',
  'submitted_count',
  'accepted_count',
  'clarification_requested_count',
  'assets_with_warnings_count',
] as const;

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
};

const throwMetricsParseError = (message: string): never => {
  // Structured client error for malformed metrics payloads (SAF-886). Do not include auth headers.
  // eslint-disable-next-line no-console
  console.error({
    event: 'asset_ropa_metrics_parse_failed',
    message,
  });
  throw new Error(message);
};

const readRequiredInteger = (metrics: Record<string, unknown>, key: string): number => {
  const value = metrics[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  return throwMetricsParseError(`Invalid integer field "${key}" in asset ropa metrics payload.`);
};

export const parseOwnerWorkspaceMetricsForFE = (raw: unknown): OwnerWorkspaceMetrics => {
  const metrics = asRecord(raw);
  if (!metrics) {
    return throwMetricsParseError('Missing workspace_metrics object.');
  }

  return {
    gdprRelevantAssetsCount: readRequiredInteger(metrics, WORKSPACE_METRIC_KEYS[0]),
    assetsWithoutProcessingActivitiesCount: readRequiredInteger(metrics, WORKSPACE_METRIC_KEYS[1]),
    incompleteProcessingsCount: readRequiredInteger(metrics, WORKSPACE_METRIC_KEYS[2]),
    dpiaIndicationsCount: readRequiredInteger(metrics, WORKSPACE_METRIC_KEYS[3]),
    thirdCountryProcessingCount: readRequiredInteger(metrics, WORKSPACE_METRIC_KEYS[4]),
    sensitivePersonalDataCount: readRequiredInteger(metrics, WORKSPACE_METRIC_KEYS[5]),
  };
};

export const parseOwnerAssetRopaMetricsForFE = (raw: unknown): OwnerAssetRopaMetrics => {
  const metrics = asRecord(raw);
  if (!metrics) {
    return throwMetricsParseError('Missing review_metrics object.');
  }

  REVIEW_CARD_METRIC_KEYS.forEach(key => {
    readRequiredInteger(metrics, key);
  });

  return parseActiveRopaReviewMetricsForFE(metrics as unknown as ActiveRopaReviewMetricsDTO);
};

export const parseOwnerReviewMetricsForFE = (raw: unknown): OwnerAssetRopaMetrics | null => {
  if (raw === null) {
    return null;
  }

  return parseOwnerAssetRopaMetricsForFE(raw);
};

/** Single parser for `/mine/` and `/mine/metrics/` nested metric objects. Ignores `metrics`. */
export const parseAssetRopaMetricsPayloadForFE = (raw: unknown): OwnerAssetRopaMetricsResponse => {
  const payload = asRecord(raw);
  if (!payload) {
    return throwMetricsParseError('Invalid asset ropa metrics payload.');
  }

  if (!Object.prototype.hasOwnProperty.call(payload, 'workspace_metrics')) {
    return throwMetricsParseError('Missing workspace_metrics on asset ropa metrics payload.');
  }

  if (!Object.prototype.hasOwnProperty.call(payload, 'review_metrics')) {
    return throwMetricsParseError('Missing review_metrics on asset ropa metrics payload.');
  }

  return {
    workspaceMetrics: parseOwnerWorkspaceMetricsForFE(payload.workspace_metrics),
    reviewMetrics: parseOwnerReviewMetricsForFE(payload.review_metrics),
  };
};

type ActiveReviewPayload = {
  id: number;
  status: RopaReviewRoundStatus;
  deadline: string;
  review_instructions?: string;
  reviewInstructions?: string;
  initiated_at?: string;
  initiatedAt?: string;
  initiated_by_membership_id?: number | null;
  initiatedByMembershipId?: number | null;
  initiated_by_display_name?: string | null;
  initiatedByDisplayName?: string | null;
};

const parseActiveReview = (raw: unknown): OwnerAssetRopaListResponse['activeReview'] => {
  const review = asRecord(raw) as ActiveReviewPayload | null;
  if (!review || typeof review.id !== 'number') {
    return null;
  }

  return {
    id: review.id,
    status: review.status,
    deadline: review.deadline,
    reviewInstructions: review.review_instructions ?? review.reviewInstructions ?? '',
    initiatedAt: review.initiated_at ?? review.initiatedAt ?? '',
    initiatedByMembershipId: review.initiated_by_membership_id ?? review.initiatedByMembershipId ?? null,
    initiatedByDisplayName: parseInitiatedByDisplayName(review.initiated_by_display_name ?? review.initiatedByDisplayName),
  };
};

const parseAssetsSource = (payload: Record<string, unknown>, data: unknown): unknown[] => {
  if (Array.isArray(payload.assets)) {
    return payload.assets;
  }

  if (Array.isArray(payload.results)) {
    return payload.results;
  }

  if (Array.isArray(data)) {
    return data;
  }

  return [];
};

const parseOwnerWorkspaceAssetForFE = (dto: AdminAssetRopaReviewListDTO & Record<string, unknown>): OwnerWorkspaceAsset => {
  const base = parseAssetRopaReviewForFE(dto);
  const gdprRelevanceStatus =
    (dto.gdpr_relevance_status as OwnerWorkspaceAsset['gdprRelevanceStatus'] | undefined) ??
    (dto.gdprRelevanceStatus as OwnerWorkspaceAsset['gdprRelevanceStatus'] | undefined) ??
    'currently_indicated';
  const informationalCompleteness =
    (dto.informational_completeness as OwnerWorkspaceAsset['informationalCompleteness'] | undefined) ??
    (dto.informationalCompleteness as OwnerWorkspaceAsset['informationalCompleteness'] | undefined) ??
    (dto.completeness_status as OwnerWorkspaceAsset['informationalCompleteness'] | undefined) ??
    (base.processingActivitiesCount > 0 ? 'incomplete' : 'no_processing_activities');

  return {
    ...base,
    gdprRelevanceStatus,
    informationalCompleteness,
    ownerName: (dto.owner_name as string | null | undefined) ?? (dto.ownerName as string | null | undefined) ?? null,
    lastUpdatedAt: String(dto.last_updated_at ?? dto.lastUpdatedAt ?? dto.updated_at ?? ''),
    lastUpdatedByName:
      (dto.last_updated_by_name as string | null | undefined) ?? (dto.lastUpdatedByName as string | null | undefined) ?? null,
    includedInCurrentReview: false,
    reviewOverlay: null,
  };
};

const parseListAssets = (payload: Record<string, unknown>, data: unknown) => {
  const assetsSource = parseAssetsSource(payload, data);

  return assetsSource.map(item => parsePermanentWorkspaceListItemForFE(item as OwnerAssetRopaListItemDTO));
};

export const parseOwnerListResponse = (data: unknown): OwnerAssetRopaListResponse => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid asset-ropa/mine response payload.');
  }

  const payload = data as Record<string, unknown>;

  let reviewMetricsRaw: unknown = null;
  if (Object.prototype.hasOwnProperty.call(payload, 'review_metrics')) {
    reviewMetricsRaw = payload.review_metrics;
  } else if (Object.prototype.hasOwnProperty.call(payload, 'reviewMetrics')) {
    reviewMetricsRaw = payload.reviewMetrics;
  }

  return {
    activeReview: parseActiveReview(payload.active_review ?? payload.activeReview),
    metrics: parseOwnerReviewMetricsForFE(reviewMetricsRaw),
    assets: parseListAssets(payload, data),
  };
};

const readCount = (payload: Record<string, unknown>, assets: unknown[]): number => {
  if (typeof payload.count === 'number' && Number.isFinite(payload.count)) {
    return payload.count;
  }

  return assets.length;
};

export const parseOwnerWorkspaceResponse = (data: unknown): OwnerWorkspaceListResponse => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid asset-ropa/mine workspace response payload.');
  }

  const payload = data as Record<string, unknown>;
  const assetsSource = parseAssetsSource(payload, data);
  const assets = assetsSource.map(item => parsePermanentWorkspaceListItemForFE(item as OwnerAssetRopaListItemDTO));

  const { reviewMetrics, workspaceMetrics } = parseAssetRopaMetricsPayloadForFE(payload);

  return {
    activeReview: parseActiveReview(payload.active_review ?? payload.activeReview),
    mode: 'permanent_workspace',
    count: readCount(payload, assetsSource),
    reviewMetrics,
    workspaceMetrics,
    assets,
  };
};

export const parseOwnerMineDetailForFE = (data: unknown): OwnerWorkspaceAsset => {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid asset-ropa/mine detail response payload.');
  }

  const nestedAsset = resolveMineDetailRecord(data) ?? asRecord(data) ?? {};

  if (isOwnerAssetRopaDetailPayload(nestedAsset) || isPermanentWorkspaceAssetPayload(nestedAsset)) {
    return parsePermanentWorkspaceAssetDetailForFE(data);
  }

  return parseOwnerWorkspaceAssetForFE(nestedAsset as AdminAssetRopaReviewListDTO & Record<string, unknown>);
};

export const parseOwnerMineMetricsResponseForFE = (data: unknown): OwnerAssetRopaMetricsResponse => {
  if (!data || typeof data !== 'object') {
    return throwMetricsParseError('Invalid asset-ropa/mine/metrics response payload.');
  }

  return parseAssetRopaMetricsPayloadForFE(data);
};

export { resolveOwnerAssetRopaResponseMode } from './assetRopaPermanentWorkspace.parsers';
